import { adminService } from '../../services/adminService';
import React from 'react';
import { Modal, Button, Row, Col, Badge, Card, ListGroup } from 'react-bootstrap';
import toast from 'react-hot-toast';

interface BookingDetailsModalProps {
  show: boolean;
  onHide: () => void;
  booking: any;
}

const BookingDetailsModal: React.FC<BookingDetailsModalProps> = ({ show, onHide, booking }) => {
  if (!booking) return null;


  const showQuickActions = booking.status === 'confirmed';


  const canResendEmail = booking.status === 'confirmed';



  // Mock notification status for this booking
  const notificationStatus = {
    email: { sent: true, delivered: true, sentAt: booking.bookingDate },
    sms: { sent: true, delivered: true, sentAt: booking.bookingDate },
    whatsapp: { sent: true, delivered: false, sentAt: booking.bookingDate }
  };

  // const handleResendNotification = (channel: string) => {
  //   toast.success(`${channel.toUpperCase()} notification sent successfully!`);
  //   console.log(`Resending ${channel} notification for booking:`, booking.id);
  // };

  // const handleResendAll = () => {
  //   toast.success('All notifications sent successfully!');
  //   console.log('Resending all notifications for booking:', booking.id);
  // };

  const handleResendNotification = async () => {
    try {
      await adminService.resendBookingEmail(booking.id);
      toast.success('Booking email resent successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend booking email');
    }
  };
  
  const getNotificationBadge = (status: { sent: boolean; delivered: boolean }) => {
    if (!status.sent) {
      return <Badge bg="secondary">Not Sent</Badge>;
    }
    if (status.delivered) {
      return <Badge bg="success"><i className="bi-check-circle me-1"></i>Delivered</Badge>;
    }
    return <Badge bg="warning"><i className="bi-clock me-1"></i>Sent</Badge>;
  };

  const paidAmount = Number(
    booking.paidAmount ??
    booking.amountValue ??
    booking.amount ??
    0
  );
  
  const couponDiscount = Number(
    booking.couponDiscount ??
    booking.pricing?.couponDiscount ??
    0
  );

  // Promotional items (e.g. Website Launch Offer — Free 35g Popcorn)
  const promotionalItems: Array<{ name: string; qty: number; unitPrice: number }> =
    (Array.isArray(booking.promotionalItems) && booking.promotionalItems) ||
    (Array.isArray(booking.bookingDetails?.promotionalItems) &&
      booking.bookingDetails.promotionalItems) ||
    [];

  // Original checkout amount before coupon
  const originalAmount = paidAmount + couponDiscount;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi-ticket-perforated me-2"></i>Booking Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Booking Information */}
        <Card className="mb-3">
          <Card.Header>
            <h6 className="mb-0">Booking Information</h6>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <small className="text-muted">Booking ID</small>
                  <div className="fw-medium">
                    <code>{booking.bookingNumber || booking.id}</code>
                  </div>
                </div>
                {booking.myvistaBookingId && (
                  <div className="mb-3">
                    <small className="text-muted">Vista Booking ID</small>
                    <div className="fw-medium">
                      <code>{booking.myvistaBookingId}</code>
                    </div>
                  </div>
                )}
                <div className="mb-3">
                  <small className="text-muted">Customer</small>
                  <div className="fw-medium">{booking.customer}</div>
                  <small className="text-muted">{booking.customerEmail}</small><br />
                  <small className="text-muted">{booking.customerMobile}</small>
                </div>
                <div className="mb-3">
                  <small className="text-muted">Movie</small>
                  <div className="fw-medium">{booking.movie}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <small className="text-muted">Cinema</small>
                  <div className="fw-medium">{booking.cinema}</div>
                </div>
                <div className="mb-3">
                  <small className="text-muted">Show Date & Time</small>
                  <div className="fw-medium">{booking.showDate} at {booking.showTime}</div>
                </div>
                <div className="mb-3">
                  <small className="text-muted">Seats</small>
                  <div>
                    {booking.seats.map((seat: string, index: number) => (
                      <Badge key={index} bg="light" text="dark" className="me-1">
                        {seat}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
            <Col md={6}>
  <div className="mb-3">
    <small className="text-muted">Payment Summary</small>

    <div className="fw-medium">

      {/* Ticket Price */}
      {Number(booking.ticketPrice) > 0 && (
        <div className="d-flex justify-content-between">
          <span>Ticket Amount</span>
          <span>₹{Number(booking.ticketPrice).toFixed(2)}</span>
        </div>
      )}

      {/* Snacks */}
      {Number(booking.snacksAmount) > 0 && (
        <>
          <div className="d-flex justify-content-between">
            <span>Snacks (incl. GST)</span>
            <span>₹{Number(booking.snacksAmount).toFixed(2)}</span>
          </div>
          {Number(booking.snacksGST) > 0 && (() => {
            const snacksBase =
              Number(booking.snacksAmount) - Number(booking.snacksGST);
            const gstPercent =
              snacksBase > 0
                ? Math.round(
                    (Number(booking.snacksGST) / snacksBase) * 100,
                  )
                : null;
            return (
              <>
                <div
                  className="d-flex justify-content-between text-muted"
                  style={{ fontSize: "0.85rem", paddingLeft: "12px" }}
                >
                  <span>Base Amount</span>
                  <span>₹{snacksBase.toFixed(2)}</span>
                </div>
                <div
                  className="d-flex justify-content-between text-muted"
                  style={{ fontSize: "0.85rem", paddingLeft: "12px" }}
                >
                  <span>
                    IGST{gstPercent !== null ? ` @ ${gstPercent}%` : ""}
                  </span>
                  <span>₹{Number(booking.snacksGST).toFixed(2)}</span>
                </div>
              </>
            );
          })()}
        </>
      )}

      {/* Promotional Items (e.g. Website Launch Offer — Free 35g Popcorn) */}
      {promotionalItems.length > 0 && (
        <div
          className="d-flex justify-content-between"
          style={{ color: "#28a745", fontWeight: 500 }}
        >
          <span>
            🎁 {promotionalItems[0].name} (Website Launch Offer)
          </span>
          <span>FREE</span>
        </div>
      )}

      {/* Convenience Fee */}
      {(Number(booking.convenienceFeeBase) > 0 ||
        Number(booking.convenienceFeeGST) > 0) && (
        <>
          <div className="d-flex justify-content-between">
            <span>Convenience Fee (incl. GST)</span>
            <span>
              ₹
              {(
                Number(booking.convenienceFeeBase || 0) +
                Number(booking.convenienceFeeGST || 0)
              ).toFixed(2)}
            </span>
          </div>
          {Number(booking.convenienceFeeBase) > 0 && (
            <div
              className="d-flex justify-content-between text-muted"
              style={{ fontSize: "0.85rem", paddingLeft: "12px" }}
            >
              <span>Base Amount</span>
              <span>₹{Number(booking.convenienceFeeBase).toFixed(2)}</span>
            </div>
          )}
          {Number(booking.convenienceFeeGST) > 0 && (
            <div
              className="d-flex justify-content-between text-muted"
              style={{ fontSize: "0.85rem", paddingLeft: "12px" }}
            >
              <span>IGST @ 18%</span>
              <span>₹{Number(booking.convenienceFeeGST).toFixed(2)}</span>
            </div>
          )}
        </>
      )}

      {/* Coupon */}
      {couponDiscount > 0 && (
        <div className="d-flex justify-content-between text-success">
          <span>Coupon Discount ({booking.couponCode || ''})</span>
          <span>-₹{couponDiscount.toFixed(2)}</span>
        </div>
      )}

      <hr />

      {/* Final Paid */}
      <div className="d-flex justify-content-between fw-bold fs-5 text-success">
        <span>Total Amount</span>
        <span>₹{paidAmount.toFixed(2)}</span>
      </div>

    </div>
  </div>
</Col>

              <Col md={6}>
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Payment Method</small>
                  {booking.paymentMethod ? (
                    <Badge
                      bg="info"
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        padding: "6px 12px",
                      }}
                    >
                      <i className="bi-credit-card-2-front-fill me-1"></i>
                      {String(booking.paymentMethod).toUpperCase()}
                    </Badge>
                  ) : (
                    <span className="text-muted">Not specified</span>
                  )}
                </div>
                <div className="mb-3">
                  <small className="text-muted">Status</small>
                  <div>
                    <Badge bg={
                      booking.status === 'confirmed' ? 'success' :
                      booking.status === 'pending' ? 'warning' :
                      booking.status === 'cancelled' ? 'danger' : 'info'
                    }>
                      {booking.status}
                    </Badge>
                    <Badge bg={
                      booking.paymentStatus === 'completed' ? 'success' :
                      booking.paymentStatus === 'pending' ? 'warning' :
                      booking.paymentStatus === 'failed' ? 'danger' : 'info'
                    } className="ms-2">
                      Payment: {booking.paymentStatus}
                    </Badge>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Notification Status */}
        {showQuickActions && (
        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="bi-bell me-2"></i>Notifications
            </h6>
            {/* <Button variant="outline-primary" size="sm" onClick={handleResendAll}>
              <i className="bi-arrow-repeat me-1"></i>Resend All
            </Button> */}
          </Card.Header>
          <Card.Body>
            {/* <small className="text-muted mb-2 d-block">
              Transactional notifications powered by <a href="https://www.brevo.com" target="_blank" rel="noopener noreferrer">Brevo</a>
            </small> */}
            <ListGroup>
              <ListGroup.Item className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <i className="bi-envelope fs-4 text-primary me-3"></i>
                  <div>
                    <div className="fw-medium">Email Notification</div>
                    <small className="text-muted">
                      {notificationStatus.email.sent ? `Sent to ${booking.customerEmail}` : 'Not sent'}
                    </small>
                  </div>
                </div>
                  <div className="d-flex align-items-center gap-2">
                    {getNotificationBadge(notificationStatus.email)}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={!canResendEmail}
                      onClick={() => {
                        if (!canResendEmail) return;
                        handleResendNotification();
                      }}
                      title={
                        canResendEmail
                          ? 'Resend booking email'
                          : 'Emails can only be sent for confirmed bookings'
                      }
                    >
                      <i className="bi-arrow-repeat"></i>
                    </Button>


                </div>
              </ListGroup.Item>

              {/* <ListGroup.Item className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <i className="bi-chat-text fs-4 text-success me-3"></i>
                  <div>
                    <div className="fw-medium">SMS Notification</div>
                    <small className="text-muted">
                      {notificationStatus.sms.sent ? `Sent to ${booking.customerMobile}` : 'Not sent'}
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {getNotificationBadge(notificationStatus.sms)}
                  <Button variant="outline-success" size="sm" onClick={() => handleResendNotification('SMS')}>
                    <i className="bi-arrow-repeat"></i>
                  </Button>
                </div>
              </ListGroup.Item> */}
{/* 
              <ListGroup.Item className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <i className="bi-whatsapp fs-4 text-success me-3"></i>
                  <div>
                    <div className="fw-medium">WhatsApp Message</div>
                    <small className="text-muted">
                      {notificationStatus.whatsapp.sent ? `Sent to ${booking.customerMobile}` : 'Not sent'}
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {getNotificationBadge(notificationStatus.whatsapp)}
                  <Button variant="outline-success" size="sm" onClick={() => handleResendNotification('WhatsApp')}>
                    <i className="bi-arrow-repeat"></i>
                  </Button>
                </div>
              </ListGroup.Item> */}
            </ListGroup>

            <div className="mt-3">
              <small className="text-muted">
                <i className="bi-info-circle me-1"></i>
                Notifications are automatically sent when booking is confirmed. Use resend if customer didn't receive them.
              </small>
            </div>
          </Card.Body>
        </Card>
        )}

        {/* Additional Actions */}
        {/* {showQuickActions && (
        <Card>
          <Card.Header>
            <h6 className="mb-0">Quick Actions</h6>
          </Card.Header>
          <Card.Body>
            <div className="d-flex flex-wrap gap-2">
              <Button variant="outline-primary" size="sm">
                <i className="bi-printer me-1"></i>Print Ticket
              </Button>
              <Button variant="outline-info" size="sm">
                <i className="bi-download me-1"></i>Download Receipt
              </Button>
              <Button variant="outline-success" size="sm">
                <i className="bi-envelope me-1"></i>Email Invoice
              </Button>

              {booking.status === 'cancelled' && booking.paymentStatus === 'completed' && (
  <Button variant="outline-warning" size="sm">
    <i className="bi-arrow-repeat me-1"></i>Refund
  </Button>
)}



            </div>
          </Card.Body>
        </Card>
        )} */}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BookingDetailsModal;

